import client from './client';

export type H5ManualTaskStatus =
  | 'issued'
  | 'copied'
  | 'claimed_sent'
  | 'verified'
  | 'rejected'
  | 'expired';

export type H5RedemptionSessionStatus =
  | 'created'
  | 'waiting_login'
  | 'logged_in'
  | 'scanning'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'destroyed';

export type H5RedemptionAuthMethod = 'qr' | 'pairing_code';

export interface H5ManualTask {
  id: string;
  campaign_id: string;
  campaign_name?: string | null;
  campaign_target_number_id: string;
  target_phone: string;
  message_text: string;
  message_nonce: string;
  points: number;
  status: H5ManualTaskStatus;
  issued_at: string;
  copied_at: string | null;
  claimed_sent_at: string | null;
  verified_at: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  redemption_id: string | null;
  pending_count?: number;
  redeem_threshold?: number;
  can_redeem?: boolean;
}

export interface H5ManualActivityOverview {
  today_confirmed_points: number;
  total_confirmed_points: number;
  pending_count: number;
  redeem_threshold: number;
  can_redeem: boolean;
  current_task: H5ManualTask | null;
  active_redemption: H5RedemptionSession | null;
}

export interface H5RedemptionSession {
  id: string;
  user_id: string;
  status: H5RedemptionSessionStatus;
  whatsapp_phone_number: string | null;
  normalized_whatsapp_phone: string | null;
  auth_method: H5RedemptionAuthMethod | null;
  wa_binding_draft_id: string | null;
  wa_connection_attempt_id: string | null;
  wa_account_id: string | null;
  pending_count: number;
  verified_count: number;
  rejected_count: number;
  confirmed_points: number;
  qr_payload: string | null;
  pairing_code: string | null;
  qr_expires_at: string | null;
  started_at: string;
  logged_in_at: string | null;
  completed_at: string | null;
  destroyed_at: string | null;
  scan_started_at: string | null;
  scan_completed_at: string | null;
  scan_lookback_hours: number;
  reward_finalized_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  tasks?: H5ManualTask[];
}

export const h5ManualActivityApi = {
  getOverview: () => client.get<{ success: true; data: H5ManualActivityOverview }>('/h5-manual-activity/overview'),
  getNextTask: () => client.post<{ success: true; data: H5ManualTask }>('/h5-manual-activity/tasks/next'),
  markCopied: (taskId: string) =>
    client.post<{ success: true; data: H5ManualTask }>(`/h5-manual-activity/tasks/${taskId}/copied`),
  markClaimedSent: (taskId: string) =>
    client.post<{ success: true; data: H5ManualTask }>(`/h5-manual-activity/tasks/${taskId}/claimed-sent`),
  createRedemptionSession: (payload: { phone_number: string; auth_method: H5RedemptionAuthMethod }) =>
    client.post<{ success: true; data: H5RedemptionSession }>('/h5-manual-activity/redemptions', payload),
  getRedemptionSession: (redemptionId: string) =>
    client.get<{ success: true; data: H5RedemptionSession }>(`/h5-manual-activity/redemptions/${redemptionId}`),
  retryRedemptionSession: (redemptionId: string) =>
    client.post<{ success: true; data: H5RedemptionSession }>(`/h5-manual-activity/redemptions/${redemptionId}/retry`),
  switchRedemptionAuthMethod: (
    redemptionId: string,
    payload: { phone_number: string; auth_method: H5RedemptionAuthMethod; force_regenerate?: boolean }
  ) =>
    client.post<{ success: true; data: H5RedemptionSession }>(
      `/h5-manual-activity/redemptions/${redemptionId}/auth-method`,
      payload
    ),
  cancelRedemptionSession: (redemptionId: string) =>
    client.post<{ success: true; data: H5RedemptionSession }>(`/h5-manual-activity/redemptions/${redemptionId}/cancel`),
};
